import { capitalize } from 'lodash';

import { LANGUAGE } from 'src/data/generation';
import { isSameLanguage } from 'src/data/generation';
import { getNonEnglishTraitButNot, getTraitLabel } from 'src/data/toneOfVoice/traits';

import { isContentImproverTool, isRewriteGenerationTool } from '../generationTool';

import {
  DEFAULT_SUGGEST_TOV_MODE,
  DRAFT_TOV_ID,
  PRESET_TONE_OF_VOICE,
  PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS,
  PRESET_TONE_OF_VOICE_PROPS,
} from './const';

export const isPresetTov = (tov) => Object.values(PRESET_TONE_OF_VOICE).includes(tov);

export const getToneOfVoiceTitle = (tov, language) => {
  if (PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].title;
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.title || '';
};
export const getToneOfVoiceDescription = (tov, language) => {
  if (PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].description;
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.description || '';
};
export const getToneOfVoiceLabel = (tov, language) => {
  if (PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language]?.[tov]) {
    return PRESET_TONE_OF_VOICE_NON_ENGLISH_PROPS[language][tov].label || '';
  }
  return PRESET_TONE_OF_VOICE_PROPS[tov]?.label || '';
};

const BUT_NOT_MAP = {
  [LANGUAGE.de]: 'aber nicht',
  [LANGUAGE.de_in]: 'aber nicht',
  [LANGUAGE.de_ch]: 'aber nicht',
  [LANGUAGE.el]: 'αλλά όχι',
  [LANGUAGE.es]: 'pero no',
  [LANGUAGE.es_la]: 'pero no',
  [LANGUAGE.es_cs]: 'pero no',
  [LANGUAGE.tr]: 'ama değil',
  [LANGUAGE.fr]: 'mais pas',
  [LANGUAGE.fr_qu]: 'mais pas',
  [LANGUAGE.bg]: 'но не',
  [LANGUAGE.cs]: 'ale ne',
  [LANGUAGE.da]: 'men ikke',
  [LANGUAGE.nl]: 'maar niet',
  [LANGUAGE.hu]: 'de nem',
  [LANGUAGE.it]: 'ma non',
  [LANGUAGE.pl]: 'ale nie',
  [LANGUAGE.pt_pt]: 'mas não',
  [LANGUAGE.pt_br]: 'mas não',
  [LANGUAGE.ro]: 'dar nu',
  [LANGUAGE.ru]: 'но не',
  [LANGUAGE.cmn]: '但不',
  [LANGUAGE.yue]: '但不',
};
const getButNotText = (language) => BUT_NOT_MAP[language] || 'but not';

export const getTovTexts = (tov, toneOfVoices, language) => {
  if (isPresetTov(tov)) {
    return {
      tovTitle: getToneOfVoiceTitle(tov, language),
      tovDescription: getToneOfVoiceDescription(tov, language),
      tovLabel: getToneOfVoiceLabel(tov, language),
    };
  } else {
    const toneOfVoice = toneOfVoices.find((toneOfVoice) => toneOfVoice.id === tov);
    return {
      tovTitle: toneOfVoice?.name,
      tovDescription: toneOfVoice?.description,
      tovLabel: toneOfVoice?.brandVoiceName,
    };
  }
};

export const getToneOfVoiceForGeneration = (selectedTov, draftTov) => {
  if (draftTov && selectedTov === DRAFT_TOV_ID) {
    return {
      draftTov: {
        personality: draftTov.personality,
        traits: draftTov.traits,
        description: draftTov.description,
      },
    };
  }
  if (isPresetTov(selectedTov)) {
    return { defaultPreset: selectedTov };
  } else {
    return { customId: selectedTov };
  }
};

export const getToneOfVoiceForInfinity = (selectedTov, toneOfVoices) => {
  if (!selectedTov) {
    return;
  } else {
    const { tovTitle } = getTovTexts(selectedTov, toneOfVoices);
    return tovTitle;
  }
};

export const getToneOfVoiceFromGeneration = ({
  tovInput,
  generationCustomModelId,
  draftToneOfVoice,
}) => {
  if (generationCustomModelId) {
    return {
      selectedCustomModelId: generationCustomModelId,
      selectedTov: null,
      draftToneOfVoice: null,
    };
  }
  if (draftToneOfVoice) {
    return {
      selectedCustomModelId: null,
      selectedTov: DRAFT_TOV_ID,
      draftToneOfVoice: { ...draftToneOfVoice, name: '' },
    };
  }
  if (tovInput?.draftTov) {
    return {
      selectedCustomModelId: null,
      selectedTov: DRAFT_TOV_ID,
      draftToneOfVoice: { ...tovInput.draftTov, name: '' },
    };
  }
  return {
    selectedCustomModelId: null,
    selectedTov: tovInput?.customId || tovInput?.defaultPreset || DEFAULT_SUGGEST_TOV_MODE,
    draftToneOfVoice: null,
  };
};

export const getToneOfVoiceForTrackingEvent = (tovInput, toneOfVoices) => {
  if (tovInput?.draftTov) {
    return {
      tovDescription: formatToneOfVoiceDescription(tovInput?.draftTov),
      tov: 'draft_tov',
    };
  }
  const customToneOfVoice = toneOfVoices.find(
    (toneOfVoice) => toneOfVoice.id === tovInput?.customId
  );
  if (customToneOfVoice) {
    return {
      tov: 'custom_tov',
      tovDescription: formatToneOfVoiceDescription(customToneOfVoice),
    };
  }
  return {
    tov: tovInput?.defaultPreset || DEFAULT_SUGGEST_TOV_MODE,
  };
};

export const formatToneOfVoiceTrait = (data, language) => {
  const traitLabel = getTraitLabel(data.trait, language);
  if (!data.butNot) {
    return capitalize(traitLabel);
  }
  return `${capitalize(traitLabel)} ${getButNotText(language)} ${getNonEnglishTraitButNot(
    data.butNot,
    language,
    data.trait
  )}`;
};

export const formatToneOfVoiceDescription = (tov, multiline, language) => {
  const result = [];
  if (tov.traits?.length) {
    result.push(
      `${tov.traits.map((trait) => `${formatToneOfVoiceTrait(trait, language)}`).join(', ')}`
    );
  }
  if (tov.personality) {
    result.push(`Personality: ${capitalize(getToneOfVoiceDescription(tov.personality, language))}`);
  }
  if (tov.description) {
    result.push(`Custom instructions: ${tov.description}`);
  }
  return result.join(multiline ? '\n' : ', ');
};

export const isEmptyTovData = (data) =>
  (!data.traits || data.traits?.length === 0) && !data.personality && !data.description;

export const getWorkspaceDefaultToneOfVoice = (workspace, generationTool) => {
  const workspaceDefaultToneOfVoice = (workspace?.defaultTovAnalyzerForTool || [])?.find(
    (item) => item.generationTool === generationTool
  );
  return workspaceDefaultToneOfVoice
    ? {
        id: workspaceDefaultToneOfVoice.customTovId,
        name: workspaceDefaultToneOfVoice.customTovName,
        ...workspaceDefaultToneOfVoice,
      }
    : null;
};

export const getBrandVoicesAssetsTonesOfVoice = (toneOfVoices, language) => {
  return toneOfVoices.filter(
    (voice) => voice.traits?.length > 0 && isSameLanguage(voice.brandVoice?.language, language)
  );
};

export const getPresetTonesOfVoice = (generationTool) => {
  const presetTonesOfVoice = Object.values(PRESET_TONE_OF_VOICE);
  if (isRewriteGenerationTool(generationTool) || isContentImproverTool(generationTool)) {
    return presetTonesOfVoice.filter(
      (toneOfVoice) => toneOfVoice !== PRESET_TONE_OF_VOICE.engaging
    );
  }
  return presetTonesOfVoice;
};
